import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { EmptyState, InlineError, Skeleton } from '../../components/ui';
import { socialKeys } from './queries';
import type { SupabaseClient } from '../../lib/supabase';

/**
 * Social Studio activity.
 *
 * The log is written by a database trigger, not by the client, so this is a
 * read-only view of what actually happened rather than of what the app
 * remembered to record.
 *
 * Capped at the most recent 200 events: this answers "what changed lately",
 * and a full audit trail belongs in a query, not in a scrolling page.
 */

const ACTIVITY_LIMIT = 200;

export interface SocialActivityRow {
  id: string;
  post_id: string;
  event_type: string;
  details: Record<string, unknown> | null;
  actor_id: string | null;
  created_at: string;
}

export async function fetchSocialActivity(client: SupabaseClient): Promise<SocialActivityRow[]> {
  const { data, error } = await client
    .from('social_activity')
    .select('id, post_id, event_type, details, actor_id, created_at')
    .order('created_at', { ascending: false })
    .limit(ACTIVITY_LIMIT);
  if (error) {
    throw new Error(error.message);
  }
  return (data ?? []) as unknown as SocialActivityRow[];
}

export function useSocialActivity() {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...socialKeys.all, 'activity'] as const,
    queryFn: () => fetchSocialActivity(client),
    retry: false,
  });
}

/** What each event says in a sentence, given its details blob. */
export function describeEvent(row: {
  event_type: string;
  details: Record<string, unknown> | null;
}): string {
  const details = row.details ?? {};
  const to = typeof details['to'] === 'string' ? details['to'] : null;
  const from = typeof details['from'] === 'string' ? details['from'] : null;

  switch (row.event_type) {
    case 'created':
      return 'created this post';
    case 'approved':
      return 'approved it';
    case 'status_changed':
      // A move with no recorded from still reads sensibly rather than showing
      // "from null to scheduled".
      return from === null
        ? `moved it to ${statusWord(to)}`
        : `moved it from ${statusWord(from)} to ${statusWord(to)}`;
    case 'assigned':
      return 'changed who is doing it';
    case 'scheduled': {
      const date = typeof details['publish_date'] === 'string' ? details['publish_date'] : null;
      return date === null ? 'set a publish date' : `set the publish date to ${date}`;
    }
    default:
      return row.event_type.replace(/_/g, ' ');
  }
}

function statusWord(status: string | null): string {
  if (status === null) return 'somewhere else';
  return status.replace(/_/g, ' ');
}

export function ActivityView({
  postTitles,
  personNames,
}: {
  postTitles: Map<string, string>;
  personNames: Map<string, string>;
}) {
  const activity = useSocialActivity();

  if (activity.isPending) {
    return <Skeleton lines={5} label="Loading activity" />;
  }
  if (activity.isError) {
    return <InlineError>Activity could not be loaded.</InlineError>;
  }
  if (activity.data.length === 0) {
    return (
      <EmptyState
        title="Nothing yet"
        description="Changes to posts show up here as people make them."
      />
    );
  }

  return (
    <ul className="social-activity-list">
      {activity.data.map((row) => (
        <li key={row.id} className="social-activity-row">
          <span className="social-activity-when">{row.created_at.slice(0, 10)}</span>
          <span className="social-activity-text">
            <strong>
              {row.actor_id === null ? 'Someone' : (personNames.get(row.actor_id) ?? 'Someone')}
            </strong>{' '}
            {describeEvent(row)}
            {' — '}
            <span className="social-activity-post">
              {postTitles.get(row.post_id) ?? 'a post that no longer exists'}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}
